import {
    InventoryDuplicateError,
    InventoryPartType,
    InventoryReferenceValidationError,
    InventoryReferenceValidationField,
    InventoryUnit,
} from '../types/inventory';

export const INVENTORY_PART_TYPE_OPTIONS: ReadonlyArray<{
    value: InventoryPartType;
    label: string;
}> = [
    { value: 'oem', label: 'OEM' },
    { value: 'a/m', label: 'A/M' },
    { value: 'rcy', label: 'RCY' },
    { value: 'oe-surplus', label: 'OE-Surplus' },
    { value: 're-man', label: 'Re-Man' },
    { value: 're-con', label: 'Re-Con' },
    { value: 'other', label: 'Other' },
];

/** Display label for inventory part-type wire values. */
export function formatInventoryPartTypeLabel(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) return '';
    const lower = trimmed.toLowerCase();
    if (lower === 'others') return 'Other';
    const byValue = INVENTORY_PART_TYPE_OPTIONS.find((opt) => opt.value === lower);
    if (byValue) return byValue.label;
    const byLabel = INVENTORY_PART_TYPE_OPTIONS.find(
        (opt) => opt.label.toLowerCase() === lower,
    );
    if (byLabel) return byLabel.label;
    return trimmed;
}

/** Normalize CSV / form input to a wire part-type value. */
export function parseInventoryPartType(
    raw: string | null | undefined,
): InventoryPartType {
    const trimmed = (raw ?? '').trim();
    if (!trimmed) return 'oem';
    const lower = trimmed.toLowerCase();
    if (lower === 'others') return 'other';
    const byValue = INVENTORY_PART_TYPE_OPTIONS.find((opt) => opt.value === lower);
    if (byValue) return byValue.value;
    const byLabel = INVENTORY_PART_TYPE_OPTIONS.find(
        (opt) => opt.label.toLowerCase() === lower,
    );
    if (byLabel) return byLabel.value;
    return 'oem';
}

export const INVENTORY_CATEGORY_OPTIONS = [
    { value: 'part', label: 'Part' },
    { value: 'material', label: 'Material' },
    { value: 'liquid', label: 'Liquid' },
] as const;

export function formatBusinessLineDisplay(businessLine?: string | null): string {
    return businessLine?.trim() ?? '';
}

export function formatStockAdjustmentRoDisplay(roNumber?: string | null): string {
    const trimmed = roNumber?.trim();
    return trimmed ? trimmed : '—';
}

/** Title Case display label for inventory unit wire values. */
export function formatInventoryUnitLabel(unit: string): string {
    if (unit === 'mL') return 'mL';
    if (unit === 'fl oz') return 'Fl oz';
    if (!unit) return '';
    return unit.charAt(0).toUpperCase() + unit.slice(1);
}

export function getInventoryStatusLabel(item: {
    status: string;
    isFlagged: boolean;
}): 'Active' | 'Flagged' | 'Discontinued' {
    if (item.status === 'discontinued') return 'Discontinued';
    if (item.isFlagged) return 'Flagged';
    return 'Active';
}

export function getInventoryStatusColorScheme(item: {
    status: string;
    isFlagged: boolean;
}): string {
    if (item.status === 'discontinued') return 'gray';
    if (item.isFlagged) return 'orange';
    return 'green';
}

export function getInventoryStatusBadgeColors(item: {
    status: string;
    isFlagged: boolean;
}): { bg: string; color: string } {
    if (item.status === 'discontinued') {
        return { bg: 'gray.200', color: 'gray.700' };
    }
    if (item.isFlagged) {
        return { bg: 'orange.200', color: 'gray.700' };
    }
    return { bg: 'green.100', color: 'gray.700' };
}

export function parseInventoryDuplicateError(error: unknown): InventoryDuplicateError | null {
    const response = (error as { response?: { status?: number; data?: unknown } })?.response;
    if (response?.status !== 409) return null;
    const data = response.data as Partial<InventoryDuplicateError> | undefined;
    if (data?.code === 'DUPLICATE_INVENTORY_ITEM' && data.existingItemId) {
        return {
            code: 'DUPLICATE_INVENTORY_ITEM',
            message:
                data.message ??
                'An item with the same location, mfg. part number, supplier, and category already exists.',
            existingItemId: data.existingItemId,
        };
    }
    return null;
}

const REFERENCE_VALIDATION_FIELDS: InventoryReferenceValidationField[] = [
    'locationId',
    'supplierId',
    'businessLine',
    'unit',
];

export function referenceValidationFieldToFormKey(
    field: InventoryReferenceValidationField,
): 'locationId' | 'supplierId' | 'unit' | 'adjustmentBusinessLine' {
    if (field === 'businessLine') return 'adjustmentBusinessLine';
    return field;
}

export function parseInventoryReferenceValidationError(
    error: unknown,
): InventoryReferenceValidationError | null {
    const response = (error as { response?: { status?: number; data?: unknown } })?.response;
    if (response?.status !== 400) return null;
    const data = response.data as Partial<InventoryReferenceValidationError> | undefined;
    if (
        data?.code === 'INVALID_REFERENCE_VALUE' &&
        data.field &&
        REFERENCE_VALIDATION_FIELDS.includes(data.field)
    ) {
        return {
            code: 'INVALID_REFERENCE_VALUE',
            message: data.message ?? 'Invalid value.',
            field: data.field,
        };
    }
    return null;
}

export function coerceInventoryUnit(
    value: string | null | undefined,
    allowedUnits: InventoryUnit[],
): InventoryUnit {
    if (value && allowedUnits.includes(value as InventoryUnit)) {
        return value as InventoryUnit;
    }
    if (allowedUnits.includes('each')) {
        return 'each';
    }
    return allowedUnits[0] ?? 'each';
}

/** Scannable Code128 value is item._id (24-char hex ObjectId). */
export const INVENTORY_ITEM_ID_PATTERN = /^[a-f0-9]{24}$/i;

export function isInventoryItemId(value: string): boolean {
    return INVENTORY_ITEM_ID_PATTERN.test(value.trim());
}
